import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface TransitionState {
  cx: number;
  cy: number;
  bg?: string;
}

export function useZoomTransition(defaultTo: string = "/", defaultDelay: number = 1100) {
  const navigate = useNavigate();
  const [zoom, setZoom] = useState<TransitionState | null>(null);
  const pendingNavRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingNav = useCallback(() => {
    if (pendingNavRef.current != null) {
      clearTimeout(pendingNavRef.current);
      pendingNavRef.current = null;
    }
  }, []);

  /**
   * Browser Back can restore this page from bfcache with zoom still active,
   * which left a stuck full-screen green overlay. Clear zoom (and any pending
   * nav timeout) on pagehide/pageshow — same idea as marketing's resetOverlay.
   */
  const resetZoom = useCallback(() => {
    clearPendingNav();
    setZoom(null);
  }, [clearPendingNav]);

  useEffect(() => {
    resetZoom();
    window.addEventListener("pagehide", resetZoom);
    window.addEventListener("pageshow", resetZoom);
    return () => {
      window.removeEventListener("pagehide", resetZoom);
      window.removeEventListener("pageshow", resetZoom);
      clearPendingNav();
    };
  }, [resetZoom, clearPendingNav]);

  const startTransition = (
    e: React.MouseEvent,
    to: string = defaultTo,
    // transitionKey accepted for legacy SPA marketing callers; unused — auth uses ?vt=
    options?: { bg?: string; transitionKey?: string; delay?: number },
  ) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const bg = options?.bg || "#fff";

    clearPendingNav();
    setZoom({ cx, cy, bg });

    pendingNavRef.current = setTimeout(() => {
      pendingNavRef.current = null;
      navigate(to);
    }, options?.delay || defaultDelay);
  };

  /**
   * For the "Zoom Out" (shrink) effect on Auth pages.
   * Absolute http(s) URLs hard-navigate (cross-origin marketing); paths use SPA navigate.
   */
  const triggerZoomOut = (cx: number, cy: number, to: string = "/", delay: number = 900) => {
    clearPendingNav();
    setZoom({ cx, cy });
    pendingNavRef.current = setTimeout(() => {
      pendingNavRef.current = null;
      if (/^https?:\/\//i.test(to)) {
        window.location.href = to;
      } else {
        navigate(to);
      }
    }, delay);
  };

  const zoomProps = zoom
    ? ({
        "--zx": `${zoom.cx}px`,
        "--zy": `${zoom.cy}px`,
        ...(zoom.bg ? { background: zoom.bg } : {}),
      } as React.CSSProperties)
    : undefined;

  return { zoom, startTransition, triggerZoomOut, zoomProps };
}
